import React, { useState } from "react";
import "./Cron.css";
import Button from "../../components/reusable/Button/Button";
import PageLoader from "../../components/reusable/PageLoader/PageLoader";
import ConfigurableTable, {
  ColumnConfig,
  ActionButton,
} from "../../components/ConfigurableTable/ConfigurableTable";
import ModalConfirmation from "../../components/reusable/ModalConfirmation/ModalConfirmation";
import { dashboardAPI } from "../../Api/dashboardAPI";
import { toastSuccess, toastError } from "../../utils/toast";

interface CronAction {
  id: string;
  label: string;
  action: () => void;
  theme:
    | "primary"
    | "red"
    | "white"
    | "blue"
    | "yellow"
    | "green"
    | "danger"
    | "warning"
    | "default";
  iconName?: string;
  disabled?: boolean;
  show?: boolean;
  toastSuccessMessage?: string;
}

interface CronItem {
  id: string;
  name: string;
  description: string;
  condition: string; // Condición de ejecución (ej: "0 2 * * *", "Diario a las 2:00 AM")
  extraConditions: string[]; // Condiciones extra requeridas para la ejecución
  actions?: CronAction[];
}

function Cron() {
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    title: "",
    dialog: "",
    onConfirm: () => {},
  });

  /*
        Condiciones de ejecución:
 
          ┌───────────── minuto (0 - 59)
          │ ┌───────────── hora (0 - 23)
          │ │ ┌───────────── día del mes (1 - 31)
          │ │ │ ┌───────────── mes (1 - 12)
          │ │ │ │ ┌───────────── día de la semana (0 - 6) (domingo=0)
          │ │ │ │ │
          3 3 * * * 
  */

  const [cronList, setCronList] = useState<CronItem[]>([
    {
      id: "1",
      name: "Mantener Neon Prendido y ejecutar novedades programadas",
      description:
        "Hace un ping a la base de datos para no dejarla entrar en modo sleep y ejecuta las novedades programadas",
      condition: "*/1 * * * *", // Cada minuto
      extraConditions: [
        'Parámetro MANTENER_NEON_PRENDIDO encendido',
        'Parámetro EJECUTAR_NOVEDADES_PROGRAMADAS encendido',
      ],
      actions: [
        {
          id: "restart",
          label: "Ejecutar novedades programadas",
          action: () => dashboardAPI.ejecutarNovedadesProgramadas(),
          theme: "green",
          iconName: "play-circle",
          disabled: false,
          show: true,
          toastSuccessMessage: "Ejecutando novedades programadas",
        },
      ],
    },
    {
      id: "2",
      name: "Busca novedades en Oracle",
      description: "Se encarga de buscar nuevas novedades en Oracle vía proxy",
      condition: "*/3 * * * *", // Cada 3 minutos
      extraConditions: [
        "Proxy de Oracle disponible",
        "Conexión a Oracle estable",
      ],
      actions: [],
    },
    {
      id: "3",
      name: "Reconstruir indices",
      description:
        "Reconstruye los índices de la base de datos para optimizar el rendimiento",
      condition: "0 */3 * * *", // Cada 3 horas
      extraConditions: [
        "Base de datos disponible",
        "Parámetro RECONSTRUIR_INDICES_AUTOMATICAMENTE encendido",
      ],
      actions: [],
    },
    {
      id: "4",
      name: "Inicializa las tareas diarias del cron",
      description:
        "Inicializa y prepara todas las tareas diarias del sistema cron",
      condition: "6 3 * * *", // Todos los días a las 3:06 AM UTC-0 (0:06 AM ARG UTC-3)
      extraConditions: ["Base de datos disponible"],
    },
    {
      id: "5",
      name: "Sincroniza las tablas mirror",
      description: "Sincroniza las tablas mirror del sistema",
      condition: "6 4 * * *", // Todos los días a las 4:06 AM UTC-0 (1:06 AM ARG UTC-3)
      extraConditions: ["Conexión a base de datos estable"],
      actions: [
        {
          id: "sincronizarTablasMirror",
          label: "Sincronizar Tablas Mirror",
          action: () => dashboardAPI.sincronizarTablasMirror(),
          theme: "green",
          iconName: "play-circle",
          disabled: false,
          show: true,
          toastSuccessMessage: "Sincronizando tablas mirror",
        },
      ],
    },
    {
      id: "6",
      name: "Revisa sincronización InfoCursos y Cupos",
      description: "Revisa la sincronización de InfoCursos y Cupos del sistema",
      condition: "6 5 * * *", // Todos los días a las 5:06 AM UTC-0 (2:06 AM ARG UTC-3)
      extraConditions: [
        "Servicio de InfoCursos disponible",
        "Conexión a base de datos estable",
      ],
      actions: [
        {
          id: "sincronizarInfoCursosyCupos",
          label: "Sincronizar InfoCursos y Cupos",
          action: () => dashboardAPI.sincronizarInfoCursosyCupos(),
          theme: "green",
          iconName: "play-circle",
          disabled: false,
          show: true,
          toastSuccessMessage: "Sincronizando InfoCursos y Cupos",
        },
      ],
    },
    {
      id: "7",
      name: "Revisar cuadratura de cupos",
      description: "Se encarga de revisar la cuadratura de cupos del sistema",
      condition: "6 6 * * *", // Todos los días a las 6:06 AM UTC-0 (3:06 AM ARG UTC-3)
      extraConditions: [
        "Datos de cupos disponibles",
        "Algoritmo de cuadratura configurado",
      ],
    },
    {
      id: "8",
      name: "Revisar sincronización de mirrors",
      description: "Actualiza contadores de la sincronización de mirrors",
      condition: "6 7 * * *", // Todos los días a las 7:06 AM UTC-0 (4:06 AM ARG UTC-3)
      extraConditions: [
        "Sistema de mirrors funcionando",
        "Base de datos disponible",
      ],
      actions: [
        {
          id: "revisarSincronizacionMirrors",
          label: "Revisar sincronización de mirrors",
          theme: "green",
          iconName: "play-circle",
          disabled: false,
          show: true,
          toastSuccessMessage: "Revisando sincronización de mirrors",
          action: () => dashboardAPI.revisarSincronizacionMirrors(),
        },
      ],
    },
  ]);

  const clearConfirmationModal = () => {
    setConfirmationModal({
      show: false,
      title: "",
      dialog: "",
      onConfirm: () => {},
    });
  };

  const handleAction = async (
    action: () => void,
    actionName: string,
    toastMessage?: string
  ) => {
    setLoadingAction(actionName);
    try {
      await action();
      if (toastMessage) {
        toastSuccess(toastMessage);
      }
    } catch (error) {
      console.error("Error executing action:", error);
      toastError("Error al ejecutar la acción");
    } finally {
      setLoadingAction(null);
    }
  };


  const getConditionDescription = (cronExpression: string) => {
    const descriptions: { [key: string]: string } = {
      "*/1 * * * *": "Cada minuto",
      "*/3 * * * *": "Cada 3 minutos",
      "0 */3 * * *": "Cada 3 horas",
      "6 3 * * *": "Diario a las 3:06 AM UTC-0 (0:06 AM ARG)",
      "6 4 * * *": "Diario a las 4:06 AM UTC-0 (1:06 AM ARG)",
      "6 5 * * *": "Diario a las 5:06 AM UTC-0 (2:06 AM ARG)",
      "6 6 * * *": "Diario a las 6:06 AM UTC-0 (3:06 AM ARG)",
      "6 7 * * *": "Diario a las 7:06 AM UTC-0 (4:06 AM ARG)",
      "0 2 * * *": "Diario a las 2:00 AM",
      "0 0 * * 0": "Domingos a medianoche",
      "0 1 1 * *": "Primer día del mes a la 1:00 AM",
      "0 9 * * 1-5": "Lunes a viernes a las 9:00 AM",
      "*/15 * * * *": "Cada 15 minutos",
      "30 3 * * 6": "Sábados a las 3:30 AM",
      "0 */6 * * *": "Cada 6 horas",
      "0 0 1 */3 *": "Cada 3 meses el día 1",
      "0 12 * * 1": "Lunes al mediodía",
      "*/5 * * * *": "Cada 5 minutos",
    };

    return descriptions[cronExpression] || "Expresión personalizada";
  };

  const getCronExpressionClass = (cronExpression: string) => {
    if (cronExpression.includes("*/")) {
      return "frequent";
    }
    if (cronExpression.includes("* * 1 *")) {
      return "monthly";
    }
    if (
      cronExpression.includes("* * * 0") ||
      cronExpression.includes("* * * 6")
    ) {
      return "weekly";
    }
    if (cronExpression.includes("* * * *")) {
      return "daily";
    }
    return "custom";
  };

  const renderCustomTable = () => {
    // Configuración de columnas
    const columns: ColumnConfig[] = [
      {
        key: "name",
        title: "Nombre",
        width: "200px",
        align: "left",
        render: (value, item) => (
          <div className="flex flex-col">
            <span className="font-semibold text-textPrimary">{value}</span>
            <span className="text-xs text-textSecondary">
              {item.description}
            </span>
          </div>
        ),
      },
      {
        key: "condition",
        title: "Condición de Ejecución",
        width: "200px",
        align: "center",
        render: (value, item) => (
          <div className="cron-condition">
            <code
              className={`cron-expression ${getCronExpressionClass(value)}`}
            >
              {value}
            </code>
            <span className="cron-description">
              {getConditionDescription(value)}
            </span>
          </div>
        ),
      },
      {
        key: "extraConditions",
        title: "Condiciones Extra",
        width: "250px",
        align: "left",
        render: (value, item) => (
          <div className="extra-conditions">
            {value && value.length > 0 ? (
              <div className="conditions-list">
                {value.map((condition: string, index: number) => (
                  <div key={index} className="condition-item">
                    <span className="condition-bullet">•</span>
                    <span className="condition-text">{condition}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="no-conditions">Sin condiciones especiales</span>
            )}
          </div>
        ),
      },
      {
        key: "actions",
        title: "Acciones",
        width: "300px",
        align: "center",
        render: (value, item) => (
          <div className="flex gap-3 justify-center flex-wrap">
            {item.actions?.map((action: CronAction) => {
              if (action.show === false) return null;

              const isDisabled =
                action.disabled ||
                loadingAction === `${action.id}-${item.id}`;

              const shouldShow = action.show === undefined || action.show === true;

              if (!shouldShow) return null;

              return (
                <Button
                  key={action.id}
                  label={action.label}
                  iconName={action.iconName}
                  theme={action.theme}
                  size="medium"
                  onClick={() =>
                    handleAction(
                      action.action,
                      `${action.id}-${item.id}`,
                      action.toastSuccessMessage
                    )
                  }
                  disabled={isDisabled}
                  loading={loadingAction === `${action.id}-${item.id}`}
                />
              );
            })}
          </div>
        ),
      },
    ];

    return (
      <ConfigurableTable
        data={cronList}
        columns={columns}
        loading={loading}
        striped={true}
        hoverable={true}
        emptyMessage="No hay tareas CRON disponibles"
      />
    );
  };

  return (
    <>
      {loading && <PageLoader inLayout={true} />}
      <div className="cron-container">
        <ModalConfirmation
          show={confirmationModal.show}
          onClose={clearConfirmationModal}
          onConfirm={confirmationModal.onConfirm}
          title={confirmationModal.title}
          message={confirmationModal.dialog}
        />

        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-textPrimary mb-2">
              Gestión de Tareas CRON
            </h1>
            <p className="text-textSecondary">
              Administra y monitorea las tareas programadas del sistema
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border-2 border-cardBorder">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-textSecondary">
                Lista de Tareas CRON
              </h2>
            </div>

            {renderCustomTable()}
          </div>
        </div>
      </div>
    </>
  );
}

export default Cron;
